// src/App.tsx

import React, { useState, useCallback, useEffect } from 'react';
import TranslationTable from './components/TranslationTable';
import { loadTranslationsFromFiles } from './utils/loadTranslations';
import { flattenTranslations, FlattenedTranslation } from './utils/flattenJson';
import { reconstructTranslations } from './utils/reconstructJson';
import './App.css';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import JSZip from 'jszip';
import { saveAs } from 'file-saver';

function App() {
  const [flattenedData, setFlattenedData] = useState<FlattenedTranslation[]>([]);
  const [filteredData, setFilteredData] = useState<FlattenedTranslation[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [additionalLanguages, setAdditionalLanguages] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(''); // Used for triggering search filtering
  const [showMissingOnly, setShowMissingOnly] = useState(false);
  const [showSameTextOnly, setShowSameTextOnly] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setIsLoading(true);
    try {
      const translations = await loadTranslationsFromFiles(files);
      const fileLangs = Object.keys(translations);

      const extraLangs = additionalLanguages
        .split(',')
        .map((l) => l.trim())
        .filter((l) => l !== '');

      const allLanguages = Array.from(new Set([...fileLangs, ...extraLangs]));
      setLanguages(allLanguages);

      const flatData = flattenTranslations(translations);
      setFlattenedData(flatData);

      const allFileNames = new Set<string>();
      for (const lang in translations) {
        for (const fileName in translations[lang]) {
          allFileNames.add(fileName);
        }
      }
      setFileNames(Array.from(allFileNames));

      toast.success('Files loaded successfully!');
    } catch (error) {
      console.error('Error loading files:', error);
      toast.error('Failed to load files.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveKey = useCallback((keyToRemove: string) => {
    setFlattenedData((prev) => prev.filter((item) => item.key !== keyToRemove));
  }, []);

  const handleUpdate = useCallback(
    (key: string, language: string, value: string) => {
      setFlattenedData((prevData) =>
        prevData.map((item) => {
          if (item.key === key) {
            return {
              ...item,
              translations: {
                ...item.translations,
                [language]: value,
              },
            };
          }
          return item;
        })
      );
    },
    []
  );

  const handleExport = async () => {
    setIsLoading(true);
    try {
      const zip = new JSZip();
      const reconstructed = reconstructTranslations(flattenedData, languages);

      for (const lang of languages) {
        const langFolder = zip.folder(lang);
        if (!langFolder) continue;
        let hasFiles = false;

        for (const fileName of fileNames) {
          const jsonContent = JSON.stringify(
            reconstructed[lang]?.[fileName] || {},
            null,
            2
          );
          if (jsonContent.trim() !== '{}') {
            langFolder.file(`${fileName}.json`, jsonContent);
            hasFiles = true;
          }
        }
        if (!hasFiles) {
          langFolder.file(
            'README.md',
            `# ${lang}\nNo translation files available.`
          );
        }
      }

      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, 'translations.zip');
      toast.success('Translations exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export translations.');
    } finally {
      setIsLoading(false);
    }
  };

  // Filtering logic triggered by checkbox changes and search button
  useEffect(() => {
    if (!flattenedData.length) {
      setFilteredData([]);
      return;
    }

    let newFiltered = [...flattenedData];

    if (showMissingOnly) {
      newFiltered = newFiltered.filter((item) =>
        languages.some(
          (lang) => !item.translations[lang] || item.translations[lang] === ''
        )
      );
    }

    if (appliedSearchTerm.trim()) {
      const st = appliedSearchTerm.toLowerCase();
      newFiltered = newFiltered.filter((item) =>
        item.key.toLowerCase().includes(st)
      );
    }

    if (showSameTextOnly) {
      newFiltered = newFiltered.filter((item) => {
        const texts = languages.map(
          (lang) => item.translations[lang]?.trim() || ''
        );
        return new Set(texts).size === 1;
      });
    }

    setFilteredData(newFiltered);
  }, [flattenedData, languages, appliedSearchTerm, showMissingOnly, showSameTextOnly]);

  return (
    <div className="app-container">
      <div className="controls">
        <label htmlFor="fileInput" className="custom-file-upload">
          📂 Select Translation Files
        </label>
        <input
          id="fileInput"
          type="file"
          multiple
          webkitdirectory=""
          onChange={handleFileChange}
          className="file-input"
        />

        <label>
          Additional Languages (comma-separated):
          <input
            type="text"
            value={additionalLanguages}
            onChange={(e) => setAdditionalLanguages(e.target.value)}
            placeholder="e.g. de, fr, es"
          />
        </label>
      </div>

      <div className="floating-bar">
        <h1 style={{ marginRight: '1rem' }}>Translation Manager</h1>

        <label style={{ marginRight: '1rem' }}>
          <input
            type="checkbox"
            checked={showMissingOnly}
            onChange={() => setShowMissingOnly(!showMissingOnly)}
          />
          Show Missing
        </label>

        <label style={{ marginRight: '1rem' }}>
          <input
            type="checkbox"
            checked={showSameTextOnly}
            onChange={() => setShowSameTextOnly(!showSameTextOnly)}
          />
          Show Same Text
        </label>

        <input
          type="text"
          placeholder="Search key..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginRight: '1rem' }}
        />
        <button onClick={() => setAppliedSearchTerm(searchTerm)}>
          Search
        </button>

        <button onClick={handleExport}>
          🚀 Export Translations
        </button>
      </div>

      {isLoading && (
        <div className="loading-indicator">
          <div className="spinner" />
          <span>Loading...</span>
        </div>
      )}

      {!isLoading && filteredData.length > 0 && (
        <TranslationTable
          data={filteredData}
          languages={languages}
          onUpdate={handleUpdate}
          onRemoveKey={handleRemoveKey}
        />
      )}

      {!isLoading && filteredData.length === 0 && (
        <p style={{ marginTop: '1rem' }}>
          No translations found or no data loaded.
        </p>
      )}

      <ToastContainer position="top-right" autoClose={5000} />
    </div>
  );
}

export default App;
